/**
 * Family a widget template belongs to. Values mirror `GsbWidgetTemplate.widgetBase`
 * as stored by the backend; `Control` is the family bound to a single property value.
 */
export var WidgetBase;
(function (WidgetBase) {
    WidgetBase[WidgetBase["EntityEdit"] = 1] = "EntityEdit";
    WidgetBase[WidgetBase["EntityList"] = 2] = "EntityList";
    WidgetBase[WidgetBase["MappedList"] = 3] = "MappedList";
    WidgetBase[WidgetBase["Container"] = 4] = "Container";
    WidgetBase[WidgetBase["Chart"] = 5] = "Chart";
    WidgetBase[WidgetBase["Content"] = 6] = "Content";
    WidgetBase[WidgetBase["Builder"] = 7] = "Builder";
    WidgetBase[WidgetBase["Page"] = 8] = "Page";
    WidgetBase[WidgetBase["Action"] = 9] = "Action";
    WidgetBase[WidgetBase["Control"] = 10] = "Control";
    WidgetBase[WidgetBase["Shell"] = 11] = "Shell";
})(WidgetBase || (WidgetBase = {}));
/**
 * Represents a widget template for UI controls
 */
export class GsbWidgetTemplate {
    _entDefName;
    id;
    name;
    title;
    description;
    icon;
    /** Backend component identifier, e.g. `GsbEditControlComponent`. */
    controlName;
    widgetBase;
    /** Discriminates variants of one `controlName`, e.g. `password`, `number`, `json`. */
    inputType;
    /** JSON string of widget settings applied before the property's own settings. */
    defaultSettings;
    /** Base64-encoded component configuration. */
    compConfiguration;
    settingsForm_id;
    multiPage;
    hidden;
    undeletable;
    module_id;
    type;
    properties;
    createDate;
    lastUpdateDate;
    createdBy_id;
    lastUpdatedBy_id;
    constructor(obj) {
        Object.assign(this, obj);
        this._entDefName = "GsbWidgetTemplate";
    }
}
/**
 * Represents a code generator for entity properties
 */
export class GsbCodeGenerator {
    _entDefName;
    id;
    name;
    title;
    description;
    code;
    createDate;
    lastUpdateDate;
    createdBy_id;
    lastUpdatedBy_id;
    constructor(obj) {
        Object.assign(this, obj);
        this._entDefName = "GsbCodeGenerator";
    }
}
/**
 * Enum for reference types that matches backend values
 */
export var RefType;
(function (RefType) {
    RefType[RefType["NoRef"] = 0] = "NoRef";
    RefType[RefType["OneToOne"] = 1] = "OneToOne";
    RefType[RefType["OneToMany"] = 2] = "OneToMany";
    RefType[RefType["ManyToOne"] = 3] = "ManyToOne";
    RefType[RefType["ManyToMany"] = 4] = "ManyToMany";
})(RefType || (RefType = {}));
/**
 * Enum for view modes
 */
export var ViewMode;
(function (ViewMode) {
    ViewMode[ViewMode["Disabled"] = 8] = "Disabled";
    ViewMode[ViewMode["Editable"] = 1] = "Editable";
    ViewMode[ViewMode["ReadOnly"] = 2] = "ReadOnly";
    ViewMode[ViewMode["Hidden"] = 4] = "Hidden";
})(ViewMode || (ViewMode = {}));
/**
 * Enum for copy actions
 */
export var CopyAction;
(function (CopyAction) {
    CopyAction[CopyAction["Default"] = 0] = "Default";
    CopyAction[CopyAction["Include"] = 1] = "Include";
    CopyAction[CopyAction["Exclude"] = 2] = "Exclude";
    CopyAction[CopyAction["Reference"] = 3] = "Reference";
})(CopyAction || (CopyAction = {}));
/**
 * Enum for screen types (bitwise)
 */
export var ScreenType;
(function (ScreenType) {
    ScreenType[ScreenType["PC"] = 1] = "PC";
    ScreenType[ScreenType["Tablet"] = 2] = "Tablet";
    ScreenType[ScreenType["Mobile"] = 4] = "Mobile";
    ScreenType[ScreenType["All"] = 7] = "All";
})(ScreenType || (ScreenType = {}));
/**
 * Enum for activity logging levels that matches backend values
 */
export var ActivityLogLevel;
(function (ActivityLogLevel) {
    ActivityLogLevel[ActivityLogLevel["None"] = 0] = "None";
    ActivityLogLevel[ActivityLogLevel["Read"] = 1] = "Read";
    ActivityLogLevel[ActivityLogLevel["Create"] = 2] = "Create";
    ActivityLogLevel[ActivityLogLevel["Update"] = 4] = "Update";
    ActivityLogLevel[ActivityLogLevel["Delete"] = 8] = "Delete";
    ActivityLogLevel[ActivityLogLevel["Execute"] = 16] = "Execute";
    ActivityLogLevel[ActivityLogLevel["List"] = 32] = "List";
})(ActivityLogLevel || (ActivityLogLevel = {}));
/**
 * Enum for data types that matches backend values
 */
export var DataType;
(function (DataType) {
    DataType[DataType["Unknown"] = 0] = "Unknown";
    DataType[DataType["Guid"] = 1] = "Guid";
    DataType[DataType["Decimal"] = 2] = "Decimal";
    DataType[DataType["Int"] = 3] = "Int";
    DataType[DataType["Long"] = 4] = "Long";
    DataType[DataType["StringUnicode"] = 5] = "StringUnicode";
    DataType[DataType["Binary"] = 6] = "Binary";
    DataType[DataType["Bool"] = 7] = "Bool";
    DataType[DataType["Enum"] = 8] = "Enum";
    DataType[DataType["DateTime"] = 9] = "DateTime";
    DataType[DataType["Reference"] = 10] = "Reference";
    DataType[DataType["Raw"] = 11] = "Raw";
    DataType[DataType["File"] = 12] = "File";
    DataType[DataType["StringASCII"] = 13] = "StringASCII";
    DataType[DataType["JSON"] = 14] = "JSON";
})(DataType || (DataType = {}));
/**
 * Represents a GSB Entity Definition
 */
export class GsbEntityDef {
    _entDefName;
    id;
    name;
    title;
    description;
    dbTableName;
    publicAccess;
    activityLogLevel;
    properties;
    isActive;
    isDeleted;
    createDate;
    lastUpdateDate;
    createdBy_id;
    lastUpdatedBy_id;
    permissions;
    propertyPermissions;
    notifications;
    workflowTriggers;
    variationType;
    splitByTenant;
    shareDataForSaas;
    sharedTenants;
    enablePermissions;
    undeletable;
    disableCopy;
    publicDefinition;
    isTracked;
    isVersioned;
    serverCached;
    clientCached;
    scriptCached;
    excludeFromBackup;
    addToUiPublish;
    addToTemplatePublish;
    parent_id;
    /** @deprecated Use `undeletable`, which matches the backend schema. */
    undeleteable;
    module_id;
    module;
    isSystem;
    defaultQuery_id;
    defaultQuery;
    constructor(obj) {
        Object.assign(this, obj);
        this._entDefName = "GsbEntityDef";
    }
}
/**
 * Represents a property of a GSB Entity Definition
 */
export class GsbProperty {
    _entDefName;
    maxLength;
    isVisible;
    lastEditTime;
    isUnique;
    defaultValue;
    refEntDef_id;
    multiControlComponent;
    definition;
    refCount;
    isPartialPrimaryKey;
    enum;
    cascadeReference;
    isEncrypted;
    name;
    scale;
    multiControlComponent_id;
    refType;
    refEntDef;
    clientValidationScript;
    dataMemberName;
    isMultiple;
    groupName;
    isIndexed;
    isMultiLingual;
    controlComponent_id;
    refEntPropName;
    fullTextIndex;
    categoryDef_id;
    formModes;
    noDeploy;
    id;
    definition_id;
    ownerEntDef_id;
    isEditable;
    isQuickEditable;
    ownerEntDef;
    createdBy_id;
    permissions;
    codeGenerator;
    contains;
    within;
    multiTableName;
    categoryDef;
    lastUpdatedBy;
    orderNumber;
    enum_id;
    description;
    title;
    serverValidationScript;
    lastUpdateDate;
    createDate;
    codeGenerator_id;
    isSystemOnly;
    createdBy;
    copyAction;
    isRequired;
    isSearchable;
    isPublic;
    controlComponent;
    undeletable;
    listScreens;
    lastUpdatedBy_id;
    defUiCtrlName;
    within_id;
    isPrimaryKey;
    isDynamic;
    availableColumn;
    regex;
    //client side properties
    createFormMode;
    viewFormMode;
    updateFormMode;
    type;
    isDefault;
    constructor(obj) {
        Object.assign(this, obj);
        this._entDefName = "GsbProperty";
    }
}
/**
 * Represents a property definition in the GSB system
 */
export class GsbPropertyDef {
    _entDefName;
    id;
    dataType;
    title;
    name;
    description;
    maxLength;
    scale;
    regex;
    usage;
    createDate;
    lastUpdateDate;
    lastUpdatedBy;
    defaultControlComponent_id;
    defaultControlComponent;
    defaultControlComponentMulti_id;
    defaultControlComponentMulti;
    module;
    createdBy;
    constructor(obj) {
        Object.assign(this, obj);
        this._entDefName = "GsbPropertyDef";
    }
}
//# sourceMappingURL=gsb-entity-def.model.js.map